"use client";
import { useAppDispatch, useAppSelector } from "../../lib/redux/hooks";
import { 
  changeProjects, 
  addSectionInForm, 
  deleteSectionInFormByIdx,
  moveSectionInForm,
  selectProjects 
} from "../../lib/redux/resumeSlice";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";

export const ProjectsForm = () => {
  const dispatch = useAppDispatch();
  const projects = useAppSelector(selectProjects);

  const handleProjectChange = (
    idx: number,
    field: keyof typeof projects[0],
    value: string | string[]
  ) => {
    dispatch(changeProjects({ idx, field, value }));
  };

  const handleAddProject = () => {
    dispatch(addSectionInForm({ form: "projects" }));
  };

  const handleDeleteProject = (idx: number) => {
    dispatch(deleteSectionInFormByIdx({ form: "projects", idx }));
  };

  const handleMoveProject = (idx: number, direction: "up" | "down") => {
    dispatch(moveSectionInForm({ form: "projects", idx, direction }));
  };

  return (
    <Card className="bg-white border-2 border-gray-800 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between bg-gray-900 text-white">
        <CardTitle className="text-lg font-semibold">Projects</CardTitle>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddProject}
          className="border-white text-white hover:bg-white hover:text-black transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Project
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {projects.map((project, idx) => (
          <div key={idx} className="space-y-4 p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-black">Project {idx + 1}</h4>
              <div className="flex items-center space-x-2">
                {projects.length > 1 && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleMoveProject(idx, "up")}
                      disabled={idx === 0}
                      className="border-gray-400 text-gray-600 hover:border-black hover:text-black transition-colors"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleMoveProject(idx, "down")}
                      disabled={idx === projects.length - 1}
                      className="border-gray-400 text-gray-600 hover:border-black hover:text-black transition-colors"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteProject(idx)}
                  disabled={projects.length === 1}
                  className="border-red-300 text-red-600 hover:border-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`project-${idx}`} className="text-black font-semibold">Project Name</Label>
                <Input
                  id={`project-${idx}`}
                  value={project.project}
                  onChange={(e) => handleProjectChange(idx, "project", e.target.value)}
                  placeholder="E-commerce Platform"
                  className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`date-${idx}`} className="text-black font-semibold">Date</Label>
                <Input
                  id={`date-${idx}`}
                  value={project.date}
                  onChange={(e) => handleProjectChange(idx, "date", e.target.value)}
                  placeholder="Jan 2023 - Mar 2023"
                  className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`descriptions-${idx}`} className="text-black font-semibold">Project Description & Technologies</Label>
              <Textarea
                id={`descriptions-${idx}`}
                value={project.descriptions.join('\n')}
                onChange={(e) => {
                  const descriptions = e.target.value.split('\n').filter(line => line.trim());
                  handleProjectChange(idx, "descriptions", descriptions);
                }}
                placeholder="• Built a full-stack e-commerce platform using React and Node.js
• Implemented user authentication and payment processing
• Deployed on AWS with CI/CD pipeline"
                rows={4}
                className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors resize-none"
              />
              <p className="text-sm text-gray-600">
                Enter each detail on a new line starting with a bullet point (•)
              </p>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
